import React, { useEffect, useMemo, useState } from "react";
import { Eye, Trash2 } from "lucide-react";
import { Pen, Search, DropDownBold } from "../AdminIcons";
import { useGetAllCategoriesAdminQuery } from "../../services/categories/categoriesApi";
import Loader from "../common/Loader";
import FormSelect from "../common/FormSelect";
import DeleteCategoryModal from "./DeleteCategoryModal";
import CategoryDetailsModal from "./CategoryDetailsModal";
import AddCategoryModal from "./AddCategoryModal";

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const CategoryTable = () => {
  const getStatusClass = (status) => {
    return status.toLowerCase() === "inactive"
      ? "bg-[#FEE2E2] text-[#991B1B]"
      : "bg-[#DCFCE7] text-[#166534]";
  };
  const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const pageLimit = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: pageLimit,
      search: debouncedSearch,
      status: statusFilter === "all" ? "" : statusFilter,
    }),
    [currentPage, debouncedSearch, statusFilter],
  );

  const { data, isLoading, isFetching, isError, error } =
    useGetAllCategoriesAdminQuery(queryParams);
  const categories = data?.data || [];
  const totalPages = data?.pagination?.totalPages || 1;
  const totalCount = data?.pagination?.total || categories.length;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModal, setIsViewModal] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (category) => {
    setSelectedCategory(category);
    setIsViewModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedCategory(null);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className=" w-full max-w-[1421px] overflow-hidden rounded-lg bg-white shadow-[0_1px_2px_0_#0000000D]">
      <div className="flex flex-wrap items-center gap-4 border-b border-[#E5E7EB] p-4 sm:gap-5">
        <div className="relative min-w-[240px] flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <Search color="#9CA3AF" className="size-[16px]" />
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search categories..."
            className="h-[38px] w-full rounded-lg border border-[#D1D5DB] bg-white pl-9 pr-3 text-sm font-normal leading-none text-[#111827] outline-none placeholder:text-[#9CA3AF]"
          />
        </div>

        <FormSelect
          id="category-status-filter"
          options={STATUS_OPTIONS}
          value={statusFilter}
          emptyByDefault={false}
          className="min-w-[150px]"
          controlWrapperClassName="mt-0"
          selectClassName="h-[38px] w-full rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm font-normal leading-none text-[#111827]"
          dropdownClassName="left-0 min-w-[150px] overflow-hidden rounded-lg border border-[#D1D5DB] bg-white"
          optionClassName="px-3 py-2 text-sm text-[#111827]"
          icon={DropDownBold}
          iconProps={{ color: "#000" }}
          onChange={setStatusFilter}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs font-medium leading-4 text-[#6B7280]">
              <th className="px-7 py-6">Name</th>

              <th className="px-7 py-6">Description</th>

              <th className="px-7 py-6">Status</th>

              <th className="px-7 py-6">Subcategories</th>

              <th className="px-7 py-6">Creation Date</th>

              <th className="pr-10 py-6 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center">
                  <div className="flex justify-center">
                    <Loader />
                  </div>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={6} className="px-7 py-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <p className="text-sm font-medium text-red-500">
                      Failed to load categories
                    </p>

                    <p className="text-xs text-[#9CA3AF]">
                      {error?.data?.message ||
                        "Something went wrong. Please try again."}
                    </p>
                  </div>
                </td>
              </tr>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <tr
                  key={category._id}
                  className="border-b border-[#E5E7EB] transition hover:bg-[#FAFAFA]"
                >
                  {/* Category */}
                  <td className="px-7 py-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden bg-[#923D8C20]">
                        {category.icon.secureUrl ? (
                          <img
                            src={category.icon.secureUrl}
                            alt="icon"
                            className=""
                          />
                        ) : null}
                      </span>

                      <span className="text-sm font-medium leading-5 text-[#111827]">
                        {category.categoryName}
                      </span>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="max-w-[368px] truncate px-7 py-4 text-sm leading-5 text-[#4B5563]">
                    {category.categoryDescription}
                  </td>

                  {/* Status */}
                  <td className="px-7 py-4">
                    <span
                      className={`inline-flex capitalize rounded-full px-2 py-1 text-xs font-semibold leading-4 ${getStatusClass(
                        category.visibility,
                      )}`}
                    >
                      {category.visibility}
                    </span>
                  </td>

                  {/* Subcategories */}
                  <td className="px-7 py-4 text-sm font-medium leading-5 text-[#111827]">
                    {category?.subCategory?.length || 0}
                  </td>

                  {/* Creation Date */}
                  <td className="px-7 py-4 text-sm leading-5 text-[#111827]">
                    {formatDate(category.createdAt)}
                  </td>

                  {/* Actions */}
                  <td className="px-7 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleViewClick(category)}
                        className="text-[#2563EB] transition hover:opacity-70"
                      >
                        <Eye size={18} strokeWidth={2} />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsUpdateModal(true);
                          setSelectedCategory(category);
                        }}
                        className="text-[#CA8A04] transition hover:opacity-70"
                      >
                        <Pen color="#CA8A04" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteClick(category)}
                        className="text-[#DC2626] transition hover:opacity-70"
                      >
                        <Trash2 size={18} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="py-12 text-center text-sm text-[#9CA3AF]"
                >
                  No categories found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalCount > pageLimit ? (
        <div className="flex items-center justify-between border-t border-[#E5E7EB] px-7 py-4">
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1 || isFetching}
            className={`rounded-full px-6 py-1.5 text-sm font-medium transition ${
              currentPage === 1 || isFetching
                ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                : "bg-[#923D8C] text-white hover:opacity-90"
            }`}
          >
            Prev
          </button>

          <p className="text-sm font-medium text-[#111827]">
            Page {currentPage}/{totalPages}
          </p>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={currentPage === totalPages || isFetching}
            className={`rounded-full px-6 py-1.5 text-sm font-medium transition ${
              currentPage === totalPages || isFetching
                ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                : "bg-[#923D8C] text-white hover:opacity-90"
            }`}
          >
            Next
          </button>
        </div>
      ) : null}

      <DeleteCategoryModal
        isOpen={isDeleteModalOpen}
        category={selectedCategory}
        onClose={handleCloseDeleteModal}
      />

      <CategoryDetailsModal
        isOpen={isViewModal}
        onClose={() => setIsViewModal(false)}
        category={selectedCategory}
      />
      <AddCategoryModal
        isOpen={isUpdateModal}
        onClose={() => setIsUpdateModal(false)}
        category={selectedCategory}
      />
    </div>
  );
};

export default CategoryTable;
